import { useQuery } from '@tanstack/react-query';
import { DriveSweepModel } from '../../../../types';
import SimpleDrawer, {
  SimpleDrawerAction,
} from '../../../shared/simple-drawer-new';
import SharedReviewLayoutMainCard, {
  MainSectionItem,
} from '../../../shared/shared-review-layout-main-card';
import SharedReviewLayoutNew, {
  ReviewSection,
} from '../../../shared/shared-review-layout-new';
import { ReviewSectionItem } from '../../../shared/shared-review-layout-item';
import AvatarChip from '../../../shared/avatar-chip';
import ChangeLogDialog from '../../../shared/change-log-dialog';
import DeleteDriveSweep from './delete-drive-sweep-dialog';
import EditDriveSweepDialog from './edit-drive-sweep/edit-drive-sweep-dialog';
import DisableDriveSweepDialog from './disable-drive-sweep-dialog';
import EnableDriveSweepDialog from './enable-drive-sweep-dialog';
import DriveSweepLogsGrid from './drive-sweep-logs-grid';
import { useDriveSweepActions } from './use-drive-sweep-actions';
import { useUserSettings } from '../../../utils/use-user-settings';
import {
  DriveRoles,
  isAbleToSeeUIElement,
} from '../../../utils/roles/client-roles';
import { Messages } from '../../../utils/message-constants';
import { getAllDriveMonitors, UserKeys } from '../../../api/users-api';
import { useMemo, useState } from 'react';
import { StatusType } from '../../../shared/status-label';
import SharedCard from '../../../shared/shared-card';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Cancel from '@mui/icons-material/Cancel';
import Check from '@mui/icons-material/Check';
import CheckCircle from '@mui/icons-material/CheckCircle';
import Delete from '@mui/icons-material/Delete';
import Edit from '@mui/icons-material/Edit';
import EditOutlined from '@mui/icons-material/EditOutlined';
import PlayArrow from '@mui/icons-material/PlayArrow';
import Update from '@mui/icons-material/Update';

interface DriveSweepDetailsDrawerProps {
  open: boolean;
  handleClose: () => void;
  selectedDriveSweep: DriveSweepModel | null;
}

export default function DriveSweepDetailsDrawer({
  open,
  handleClose,
  selectedDriveSweep,
}: Readonly<DriveSweepDetailsDrawerProps>) {
  const { driveRoles, changeLogRoles } = useUserSettings();

  const {
    handleStartDriveSweep,
    handleRunWithChangeLog,
    isStartingSweep,
    openChangeLogDialog,
    setOpenChangeLogDialog,
    changeLogMessage,
    setChangeLogMessage,
    setDriveSweepToStart,
  } = useDriveSweepActions();

  const [activeTab, setActiveTab] = useState(0);
  const [openEdit, setOpenEdit] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [openEnable, setOpenEnable] = useState(false);
  const [openDisable, setOpenDisable] = useState(false);

  const { data: sweepData } = useQuery({
    queryKey: [UserKeys.DriveSweepModels],
    queryFn: getAllDriveMonitors,
    enabled: !!selectedDriveSweep,
  });
  const currentSweep =
    sweepData?.find((s) => s.folderId === selectedDriveSweep?.folderId) ??
    selectedDriveSweep;

  const canEdit = isAbleToSeeUIElement(
    driveRoles || [],
    DriveRoles.CanEditDriveSweep.value
  );

  const canStart = isAbleToSeeUIElement(
    driveRoles || [],
    DriveRoles.CanStartDriveSweep.value
  );

  const canRun = canStart && !currentSweep?.isRunning;

  const canToggle = isAbleToSeeUIElement(
    driveRoles || [],
    DriveRoles.UpdateDriveSweepStatus.value
  );
  const canDelete = isAbleToSeeUIElement(
    driveRoles || [],
    DriveRoles.CanDeleteDriveSweep.value
  );

  const runDisabledReason = !canStart
    ? Messages.Errors.Permission.Acting(DriveRoles.CanStartDriveSweep.name)
    : currentSweep?.isRunning
    ? 'Sweep is currently running'
    : undefined;
  const editDisabledReason = canEdit
    ? undefined
    : Messages.Errors.Permission.Acting(DriveRoles.CanEditDriveSweep.name);
  const toggleDisabledReason = canToggle
    ? undefined
    : Messages.Errors.Permission.Acting(DriveRoles.UpdateDriveSweepStatus.name);
  const deleteDisabledReason = canDelete
    ? undefined
    : Messages.Errors.Permission.Acting(DriveRoles.CanDeleteDriveSweep.name);

  const mainSection: MainSectionItem = useMemo(() => {
    if (!currentSweep) return { mainTitle: '' };

    return {
      mainTitle: currentSweep.name,
      description: currentSweep.desc || 'No description provided',
      status: (currentSweep.isRunning
        ? 'INPROGRESS'
        : currentSweep.isActive
        ? 'ENABLED'
        : 'DISABLED') as StatusType,
      fields: [
        {
          label: 'Last Run',
          value: currentSweep.lastRun
            ? new Date(currentSweep.lastRun).toLocaleString()
            : 'Never',
          icon: Update,
        },
        {
          label: 'Created',
          value: new Date(currentSweep.created).toLocaleString(),
          icon: Check,
        },
        ...(currentSweep.lastUpdated
          ? [
              {
                label: 'Last Updated',
                value: new Date(currentSweep.lastUpdated).toLocaleString(),
                icon: EditOutlined,
              },
            ]
          : []),
      ],
    };
  }, [currentSweep]);

  const leftSections: ReviewSection[] = useMemo(() => {
    if (!currentSweep) return [];

    const leftItems: ReviewSectionItem[] = [
      {
        label: 'Folder',
        value: currentSweep.folderName ? (
          <AvatarChip
            type="User Drive"
            email={currentSweep.folderName}
            size="small"
          />
        ) : (
          'N/A'
        ),
      },
      {
        label: 'Owner:',
        value: <AvatarChip email={currentSweep.ownerId} type="User" />,
      },
      {
        label: 'Created By:',
        value: <AvatarChip email={currentSweep.createdBy} type="User" />,
      },
    ];

    if (currentSweep.updatedBy) {
      leftItems.push({
        label: 'Last Updated By:',
        value: <AvatarChip email={currentSweep.updatedBy} type="User" />,
      });
    }

    return [
      {
        title: 'Configuration',
        leftItems,
      },
    ];
  }, [currentSweep]);

  const actionsMenu: SimpleDrawerAction[] | undefined = currentSweep
    ? [
        {
          label: 'Run',
          icon: <PlayArrow fontSize="small" />,
          onClick: () => {
            handleStartDriveSweep(currentSweep, changeLogRoles);
          },
          disabled: !canRun,
          disabledReason: runDisabledReason,
        },
        {
          label: 'Edit',
          icon: <Edit fontSize="small" />,
          onClick: () => {
            setOpenEdit(true);
          },
          disabled: !canEdit,
          disabledReason: editDisabledReason,
        },
        currentSweep.isActive
          ? {
              label: 'Disable',
              icon: <Cancel fontSize="small" />,
              onClick: () => {
                setOpenDisable(true);
              },
              disabled: !canToggle,
              disabledReason: toggleDisabledReason,
            }
          : {
              label: 'Enable',
              icon: <CheckCircle fontSize="small" />,
              onClick: () => {
                setOpenEnable(true);
              },
              disabled: !canToggle,
              disabledReason: toggleDisabledReason,
            },
        {
          label: 'Delete',
          icon: <Delete fontSize="small" />,
          onClick: () => {
            setOpenDelete(true);
          },
          disabled: !canDelete,
          disabledReason: deleteDisabledReason,
        },
      ]
    : undefined;

  return (
    <>
      <SimpleDrawer
        title="Drive Sweep Details"
        open={open}
        onClose={handleClose}
        actionsMenu={actionsMenu}
      >
        {selectedDriveSweep && currentSweep && (
          <Stack spacing={2}>
            <SharedReviewLayoutMainCard section={mainSection} isDrawer={true} />

            <SharedCard>
              <Box sx={{ borderBottom: 1, borderColor: 'divider', mx: 2 }}>
                <Tabs
                  value={activeTab}
                  onChange={(_e, val) => setActiveTab(val)}
                >
                  <Tab label="Overview" />
                  <Tab label="Logs" />
                </Tabs>
              </Box>

              <Box sx={{ pt: 2, pb: 4, px: 2 }}>
                {activeTab === 0 && (
                  <SharedReviewLayoutNew
                    leftSections={leftSections}
                    isDrawer={true}
                    variant="light"
                  />
                )}
                {activeTab === 1 && (
                  <DriveSweepLogsGrid
                    folderId={currentSweep.folderId}
                    height={560}
                  />
                )}
              </Box>
            </SharedCard>
          </Stack>
        )}
      </SimpleDrawer>

      <ChangeLogDialog
        open={openChangeLogDialog}
        handleClose={() => {
          setOpenChangeLogDialog(false);
          setDriveSweepToStart(null);
        }}
        changeLogMessage={changeLogMessage}
        setChangeLogMessage={setChangeLogMessage}
        mutate={handleRunWithChangeLog}
        mutateLoading={isStartingSweep}
      />

      {openEdit && currentSweep && (
        <EditDriveSweepDialog
          open={openEdit}
          handleClose={() => setOpenEdit(false)}
          selectedDriveSweep={currentSweep}
          changeLogRoles={changeLogRoles}
        />
      )}

      {openDelete && currentSweep && (
        <DeleteDriveSweep
          open={openDelete}
          handleClose={() => setOpenDelete(false)}
          selectedDriveSweeps={[currentSweep]}
          changeLogRoles={changeLogRoles}
          onSuccess={handleClose}
        />
      )}

      {openEnable && currentSweep && (
        <EnableDriveSweepDialog
          open={openEnable}
          handleClose={() => setOpenEnable(false)}
          selectedDriveSweeps={[currentSweep]}
          setSelectedRows={() => {}}
          changeLogRoles={changeLogRoles}
        />
      )}

      {openDisable && currentSweep && (
        <DisableDriveSweepDialog
          open={openDisable}
          handleClose={() => setOpenDisable(false)}
          selectedDriveSweeps={[currentSweep]}
          setSelectedRows={() => {}}
          changeLogRoles={changeLogRoles}
        />
      )}
    </>
  );
}
